import React, { useState, useEffect } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Package, Users, Pencil, RotateCcw, Link2, Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useToast } from '@/hooks/useToast'
import type { OrganisationWithDetails, OrganisationUser } from '../index'
import ModuleUsersModal from './ModuleUsersModal'
import CreateModuleModal from './CreateModuleModal'
import JoinLinksModal from './JoinLinksModal'

interface ModulesTabProps {
  organisation: OrganisationWithDetails | null
  loading: boolean
  onRefresh: () => void
}

/** One active subscription of the organisation: its module, and the users seated in that subscription. */
export interface ModuleWithUsers {
  id: number
  name: string
  subscriptionId: number
  dueDate: string
  connectedUsers: OrganisationUser[]
  userCount: number
  maxUsers: number
  coachFlowId: number
  roleplayFlowId: number
  reviewPromptPayloadId: number
  timerDuration: number
  defaultRemainingTime: number
  reviewPageSize: number
  microfankProjectId?: number | null
}

const GET_ORGANISATION_MODULES = gql`
  query getOrganisationModules($organisationId: Int!) {
    getOrganisationModules(organisationId: $organisationId) {
      id
      name
      subscriptionId
      dueDate
      userCount
      maxUsers
      coachFlowId
      roleplayFlowId
      reviewPromptPayloadId
      timerDuration
      defaultRemainingTime
      reviewPageSize
      microfankProjectId
      connectedUsers {
        id
        email
        firstName
        lastName
        isActive
        createdAt
        updatedAt
      }
    }
  }
`

const CLEAR_MODULE_WELCOME_CACHE = gql`
  mutation clearModuleWelcomeCache($moduleId: Int!) {
    clearModuleWelcomeCache(moduleId: $moduleId) {
      success
      message
    }
  }
`

const ModulesTab: React.FC<ModulesTabProps> = ({ organisation, loading, onRefresh }) => {
  const [modules, setModules] = useState<ModuleWithUsers[]>([])
  const [loadingModules, setLoadingModules] = useState(false)
  const [selectedModule, setSelectedModule] = useState<ModuleWithUsers | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editModule, setEditModule] = useState<ModuleWithUsers | null>(null)
  const [clearingCacheId, setClearingCacheId] = useState<number | null>(null)
  const [isJoinLinksOpen, setIsJoinLinksOpen] = useState(false)
  const [joinLinksModuleId, setJoinLinksModuleId] = useState<number | null>(null)
  const { showSuccess, showError } = useToast()
  const { t } = useTranslation()

  useEffect(() => {
    if (organisation?.id) {
      fetchModules()
    }
  }, [organisation?.id])

  const fetchModules = async () => {
    if (!organisation) return

    try {
      setLoadingModules(true)
      const response = await authorizedGraphQLRequest<{
        getOrganisationModules: ModuleWithUsers[]
      }>(GET_ORGANISATION_MODULES, { organisationId: organisation.id })
      const newModules = response.getOrganisationModules || []
      setModules(newModules)

      // Update selectedModule with fresh data if modal is open
      if (selectedModule) {
        const updatedModule = newModules.find((m) => m.subscriptionId === selectedModule.subscriptionId)
        if (updatedModule) {
          setSelectedModule(updatedModule)
        }
      }
    } catch (error) {
      console.error('Failed to fetch modules:', error)
    } finally {
      setLoadingModules(false)
    }
  }

  const handleModuleClick = (module: ModuleWithUsers) => {
    setSelectedModule(module)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedModule(null)
  }

  const handleModuleUpdate = () => {
    fetchModules()
    onRefresh()
  }

  const handleEditClick = (e: React.MouseEvent, module: ModuleWithUsers) => {
    e.stopPropagation()
    setEditModule(module)
    setIsCreateModalOpen(true)
  }

  const handleClearCache = async (e: React.MouseEvent, module: ModuleWithUsers) => {
    e.stopPropagation()
    // Bulk action: wipes every target's welcome in the module, so confirm before running.
    if (!confirm(`Reset welcome cache for all targets in "${module.name}"? They will regenerate on next open.`)) return
    try {
      setClearingCacheId(module.id)
      await authorizedGraphQLRequest(CLEAR_MODULE_WELCOME_CACHE, { moduleId: module.id })
      showSuccess('Module welcome cache cleared')
    } catch (error) {
      console.error('Failed to clear module welcome cache:', error)
      showError('Failed to clear module welcome cache')
    } finally {
      setClearingCacheId(null)
    }
  }

  const handleJoinLinksClick = (e: React.MouseEvent, module?: ModuleWithUsers) => {
    // The card's own onClick opens ModuleUsersModal.
    e.stopPropagation()
    setJoinLinksModuleId(module?.id ?? null)
    setIsJoinLinksOpen(true)
  }

  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false)
    setEditModule(null)
  }

  const handleCreateSuccess = () => {
    fetchModules()
    onRefresh()
  }

  if (loading || loadingModules) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!organisation) {
    return <div className="text-center text-gray-500 py-12">No organisation selected</div>
  }

  // A module with two active subscriptions has two cards, but is one module.
  const uniqueModules = [...new Map(modules.map((m) => [m.id, m])).values()]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h4 className="font-semibold text-gray-800">Available Modules ({uniqueModules.length})</h4>
        <button
          onClick={handleJoinLinksClick}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors"
        >
          <Link2 className="h-4 w-4" />
          {t('joinLinks.title')}
        </button>
      </div>

      {/* Modules List */}
      {modules.length === 0 ? (
        <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-xl">
          <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No active module subscriptions</p>
          <p className="text-sm mt-1">Create a subscription to see modules here</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {modules.map((module) => {
            const usagePercent = module.maxUsers > 0 ? (module.userCount / module.maxUsers) * 100 : 0
            const isAtCapacity = module.userCount >= module.maxUsers

            return (
              <div
                key={module.subscriptionId}
                onClick={() => handleModuleClick(module)}
                className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all cursor-pointer hover:border-primary/30"
              >
                {/* Module Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-primary to-primary/70 rounded-lg">
                      <Package className="h-5 w-5 text-white" />
                    </div>
                    <h5 className="font-semibold text-gray-800">{module.name}</h5>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => handleJoinLinksClick(e, module)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                      title={t('joinLinks.forModule')}
                    >
                      <Link2 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={(e) => handleEditClick(e, module)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" title="Edit module config">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => handleClearCache(e, module)}
                      disabled={clearingCacheId === module.id}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                      title="Reset welcome cache"
                    >
                      <RotateCcw className={`h-3.5 w-3.5 ${clearingCacheId === module.id ? 'animate-spin' : ''}`} />
                    </button>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${isAtCapacity ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                      <Users className="h-3 w-3" />
                      {module.userCount} / {module.maxUsers}
                    </span>
                  </div>
                </div>

                {/* Config Summary */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700">Coach: {module.coachFlowId}</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700">RP: {module.roleplayFlowId}</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700">Timer: {module.timerDuration}s</span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div className={`h-2 rounded-full transition-all ${isAtCapacity ? 'bg-red-500' : 'bg-primary'}`} style={{ width: `${Math.min(usagePercent, 100)}%` }} />
                </div>

                {/* Usage Text */}
                <p className="text-xs text-gray-500 flex justify-between gap-2">
                  <span>{isAtCapacity ? 'At capacity - remove users to add more' : `${module.maxUsers - module.userCount} seats available`}</span>
                  <span className="inline-flex items-center gap-1 whitespace-nowrap">
                    <Calendar className="h-3 w-3" />
                    Due: {new Date(module.dueDate).toLocaleDateString()}
                  </span>
                </p>
              </div>
            )
          })}
        </div>
      )}

      {/* Module Users Modal */}
      {selectedModule && organisation && <ModuleUsersModal isOpen={isModalOpen} onClose={handleCloseModal} module={selectedModule} organisation={organisation} onUpdate={handleModuleUpdate} />}

      {/* Create/Edit Module Modal */}
      <CreateModuleModal isOpen={isCreateModalOpen} onClose={handleCreateModalClose} onSuccess={handleCreateSuccess} editModule={editModule} />

      {/* Join Links Modal */}
      {organisation && (
        <JoinLinksModal isOpen={isJoinLinksOpen} onClose={() => setIsJoinLinksOpen(false)} organisation={organisation} modules={uniqueModules} preselectedModuleId={joinLinksModuleId} />
      )}
    </div>
  )
}

export default ModulesTab
